import { sameJson } from '@components/Pages/SchedulerPage/lib/sameJson';

// ---------------------------------------------------------------------------
// Id-aware structural deltas over the scheduler's data (QA F-44 / F-45 / F-70 /
// F-71).
//
// A delta records WHAT an edit changed, addressed by key and by entity id —
// never by array index and never as a whole-document snapshot. Three things
// are built on it:
//
// - Undo / redo. An entry holds the delta of one undoable action, so undoing
//   it reverts exactly the fields that action set. A write saved instantly in
//   between (a status, an area, a crew size) is a different field, or the same
//   field with a newer value, and is left alone — the old whole-`bySite`
//   snapshot rolled every one of them back.
// - The stale-copy rebase. The user's unsaved edits are the delta from the
//   saved baseline to the working copy; replaying it on the fresh doc carries
//   over deletions, additions and every field edit, dependencies included.
// - Save. Edits made while the PUT was in flight are the delta from what was
//   sent to the working copy; replaying it on the saved doc keeps them.
//
// Shapes. A plain object diffs key by key. An array whose elements are all
// objects with a unique string `id` (tasks, groups, dependencies, markers) is a
// COLLECTION: elements are matched by id, and an element's fields are each
// compared as a whole value — a checklist or a profession snapshot is one field
// to the user, so it is kept or replaced as one. Anything else is a value.
// ---------------------------------------------------------------------------

/** A value replaced as a whole; `undefined` on either side means "absent". */
export interface ISetDelta {
  kind: 'set';
  before: unknown;
  after: unknown;
}

/** Per-key changes of a plain object. */
export interface IObjectDelta {
  kind: 'object';
  keys: Record<string, TreeDelta>;
}

/** An element that entered or left a collection, with the id it sat after. */
export interface ICollectionEntry {
  id: string;
  value: unknown;
  /** The id of the element just before it (null: it was first). */
  after: string | null;
}

/** Elements added, removed and changed in an id-keyed collection. */
export interface ICollectionDelta {
  kind: 'collection';
  added: ICollectionEntry[];
  removed: ICollectionEntry[];
  changed: { id: string; fields: IObjectDelta }[];
}

export type TreeDelta = ISetDelta | IObjectDelta | ICollectionDelta;

/**
 * How a delta is replayed. `inverse` undoes it. `guarded` reverts (or re-applies)
 * a value only while it still holds what the delta left there — the undo
 * semantics: a newer write to the same field wins. Unguarded replay always
 * writes — the rebase semantics: the user's edit wins over the fresh value.
 */
export interface IReplay {
  direction: 'forward' | 'inverse';
  guarded: boolean;
}

/** Undo: revert, keeping any value written since. */
export const UNDO: IReplay = { direction: 'inverse', guarded: true };
/** Redo: re-apply, keeping any value written since. */
export const REDO: IReplay = { direction: 'forward', guarded: true };
/** Rebase: re-apply the user's edits over another copy, the user's edit winning. */
export const MERGE: IReplay = { direction: 'forward', guarded: false };

type Plain = Record<string, unknown>;
type Identified = Plain & { id: string };

function isPlain(value: unknown): value is Plain {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCollection(value: unknown): value is Identified[] {
  if (!Array.isArray(value)) return false;
  const ids = new Set<string>();
  for (const element of value) {
    if (!isPlain(element) || typeof element.id !== 'string' || ids.has(element.id)) return false;
    ids.add(element.id);
  }
  return true;
}

function diffValue(before: unknown, after: unknown): ISetDelta | null {
  return before === after || sameJson(before, after) ? null : { kind: 'set', before, after };
}

function diffObject(before: Plain, after: Plain, diffChild: (a: unknown, b: unknown) => TreeDelta | null): IObjectDelta | null {
  const keys: Record<string, TreeDelta> = {};
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const delta = diffChild(before[key], after[key]);
    if (delta) keys[key] = delta;
  }
  return Object.keys(keys).length > 0 ? { kind: 'object', keys } : null;
}

/** The elements of `list` that `pick` selects, each with the id it sat after. */
function entriesOf(list: Identified[], pick: (element: Identified) => boolean): ICollectionEntry[] {
  const entries: ICollectionEntry[] = [];
  list.forEach((element, index) => {
    if (pick(element)) entries.push({ id: element.id, value: element, after: index === 0 ? null : list[index - 1]!.id });
  });
  return entries;
}

function diffCollection(before: Identified[], after: Identified[]): TreeDelta | null {
  const beforeById = new Map(before.map((element) => [element.id, element]));
  const afterById = new Map(after.map((element) => [element.id, element]));
  const keptBefore = before.filter((element) => afterById.has(element.id)).map((element) => element.id);
  const keptAfter = after.filter((element) => beforeById.has(element.id)).map((element) => element.id);
  // A reorder has no by-id shape: record it as a whole-list replacement.
  if (!sameJson(keptBefore, keptAfter)) return { kind: 'set', before, after };

  const changed: ICollectionDelta['changed'] = [];
  for (const element of after) {
    const previous = beforeById.get(element.id);
    const fields = previous && diffObject(previous, element, diffValue);
    if (fields) changed.push({ id: element.id, fields });
  }
  const removed = entriesOf(before, (element) => !afterById.has(element.id));
  const added = entriesOf(after, (element) => !beforeById.has(element.id));
  if (removed.length === 0 && added.length === 0 && changed.length === 0) return null;
  return { kind: 'collection', added, removed, changed };
}

/** What changed from `before` to `after`, or null when nothing did. */
export function diffTree(before: unknown, after: unknown): TreeDelta | null {
  if (before === after) return null;
  if (isCollection(before) && isCollection(after)) return diffCollection(before, after);
  if (isPlain(before) && isPlain(after)) return diffObject(before, after, diffTree);
  return diffValue(before, after);
}

function replaySet(target: unknown, delta: ISetDelta, replay: IReplay): unknown {
  const [from, to] = replay.direction === 'forward' ? [delta.before, delta.after] : [delta.after, delta.before];
  if (replay.guarded && !sameJson(target, from)) return target;
  return to;
}

function replayObject(target: unknown, delta: IObjectDelta, replay: IReplay): unknown {
  // The object this delta edits is gone (its task deleted, its record dropped).
  if (!isPlain(target)) return target;
  let result: Plain = target;
  for (const [key, child] of Object.entries(delta.keys)) {
    const value = replayDelta(target[key], child, replay);
    if (value === target[key]) continue;
    if (result === target) result = { ...target };
    if (value === undefined) delete result[key];
    else result[key] = value;
  }
  return result;
}

/** Where an entry goes back in: after the element it sat after, or — that one gone — at the end. */
function insertionIndex(list: Identified[], after: string | null): number {
  if (after === null) return 0;
  const anchor = list.findIndex((element) => element.id === after);
  return anchor === -1 ? list.length : anchor + 1;
}

function replayCollection(target: unknown, delta: ICollectionDelta, replay: IReplay): unknown {
  // An absent collection is an empty one (a doc with no markers yet).
  const list: Identified[] = Array.isArray(target) ? (target as Identified[]) : [];
  const [leaving, entering] = replay.direction === 'forward' ? [delta.removed, delta.added] : [delta.added, delta.removed];
  const leavingIds = new Set(leaving.map((entry) => entry.id));
  const changedById = new Map(delta.changed.map((entry) => [entry.id, entry.fields]));
  let touched = false;
  const result: Identified[] = [];
  for (const element of list) {
    if (leavingIds.has(element.id)) {
      touched = true;
      continue;
    }
    const fields = changedById.get(element.id);
    const next = fields ? (replayObject(element, fields, replay) as Identified) : element;
    if (next !== element) touched = true;
    result.push(next);
  }
  for (const entry of entering) {
    // Already there: the other copy made the same addition.
    if (result.some((element) => element.id === entry.id)) continue;
    result.splice(insertionIndex(result, entry.after), 0, entry.value as Identified);
    touched = true;
  }
  return touched ? result : target;
}

/**
 * Replay `delta` onto `target`, returning the new value. Untouched branches
 * keep their references, so memoized selectors downstream stay quiet.
 */
export function replayDelta(target: unknown, delta: TreeDelta, replay: IReplay): unknown {
  switch (delta.kind) {
    case 'set':
      return replaySet(target, delta, replay);
    case 'object':
      return replayObject(target, delta, replay);
    case 'collection':
      return replayCollection(target, delta, replay);
  }
}
